import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock3, Copy, Flag, Loader2, Plus, ShieldAlert } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { adminPlatformSettingService } from "@/services/admin-platform-setting.service";
import {
  FEATURE_FLAGS,
  isFeatureFlagEnabled,
  type PlatformSettingListDto,
} from "@/types/platform-setting";

// The backend keeps each evaluated flag for up to 60 seconds. Client pages
// apply the latest configuration on their next feature-config fetch.
const PROPAGATION_TOAST =
  "Feature flag updated — applies after the server cache expires and the client refreshes configuration.";

type PendingToggle = {
  flag: Pick<(typeof FEATURE_FLAGS)[number], "label" | "offConsequence">;
  row: PlatformSettingListDto | undefined;
  newValue: boolean;
};

export function FeatureFlagsTab() {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingToggle | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedFlagType, setSelectedFlagType] = useState<string>("");
  const [customKey, setCustomKey] = useState("");
  const [customDescription, setCustomDescription] = useState("");

  // A dedicated endpoint returns only global feature flags. This avoids one
  // database/API round-trip per card and cannot be paginated away by unrelated
  // platform settings.
  const featureFlagsQuery = useQuery({
    queryKey: ["admin", "platform-settings", "feature-flags"],
    queryFn: () => adminPlatformSettingService.getFeatureFlags(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, newValue }: { id: string; newValue: boolean }) =>
      // P0 fix: ALWAYS IsActive=true. The toggle writes Value and forces
      // IsActive true so an inactive row can never silently re-enable a flag.
      adminPlatformSettingService.updateSetting(id, {
        value: newValue ? "true" : "false",
        isActive: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "platform-settings"],
      });
      toast.success(PROPAGATION_TOAST);
      setPending(null);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const createMutation = useMutation({
    mutationFn: (flag: (typeof FEATURE_FLAGS)[number]) =>
      adminPlatformSettingService.createSetting({
        type: flag.type,
        key: flag.settingKey,
        value: "true",
        description: flag.description,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "platform-settings"],
      });
      toast.success("Feature flag setting created and enabled.");
      setIsCreateOpen(false);
      setSelectedFlagType("");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const createCustomMutation = useMutation({
    mutationFn: () =>
      adminPlatformSettingService.createSetting({
        type: "CustomFeatureFlag",
        key: customKey.trim(),
        value: "false",
        description: customDescription.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "platform-settings"],
      });
      toast.success("Custom feature flag configuration created.");
      setIsCreateOpen(false);
      setCustomKey("");
      setCustomDescription("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleToggle = (
    flag: (typeof FEATURE_FLAGS)[number],
    row: PlatformSettingListDto | undefined,
  ) => {
    const current = isFeatureFlagEnabled(row);
    setPending({ flag, row, newValue: !current });
  };

  const handleConfirm = () => {
    if (!pending) return;
    if (!pending.row) {
      toast.error("Setting row not found. Please refresh and try again.");
      setPending(null);
      return;
    }
    updateMutation.mutate({
      id: pending.row.id,
      newValue: pending.newValue,
    });
  };

  const missingFlags = FEATURE_FLAGS.filter(
    (flag) =>
      !featureFlagsQuery.data?.some((setting) => setting.type === flag.type),
  );
  const selectedFlag = missingFlags.find(
    (flag) => flag.type === selectedFlagType,
  );
  const customRows =
    featureFlagsQuery.data?.filter(
      (setting) => setting.type === "CustomFeatureFlag",
    ) ?? [];

  const copyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      toast.success("Feature flag key copied.");
    } catch {
      toast.error("Could not copy the feature flag key.");
    }
  };

  return (
    <div className="space-y-4">
      <section className="rounded border border-primary/15 bg-primary/[0.035] px-4 py-3 sm:px-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Flag className="size-4" aria-hidden="true" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold tracking-tight">
                Feature Flags
              </h2>
              <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                Control which client-facing capabilities are available across
                every tenant.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-3 sm:pt-1">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <Clock3 className="size-3.5" aria-hidden="true" />
              Server cache: up to ~1 minute
            </div>
            <Button size="sm" onClick={() => setIsCreateOpen(true)}>
              <Plus className="mr-1.5 size-3.5" /> Add flag configuration
            </Button>
          </div>
        </div>
      </section>

      <div className="grid gap-3 lg:grid-cols-2">
        {FEATURE_FLAGS.map((flag) => {
          const row = featureFlagsQuery.data?.find(
            (setting) => setting.type === flag.type,
          );
          const enabled = isFeatureFlagEnabled(row);

          return (
            <Card
              key={flag.type}
              className="group overflow-hidden shadow-sm transition-shadow hover:shadow-md"
            >
              <CardHeader className="p-3.5 pb-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-sm">{flag.label}</CardTitle>
                      <Badge
                        variant={enabled ? "default" : "outline"}
                        className={`px-2 py-0 text-[11px] ${
                          enabled
                            ? "bg-emerald-600 text-white hover:bg-emerald-600"
                            : "border-muted-foreground/30 bg-muted/50 text-muted-foreground"
                        }`}
                      >
                        {enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                    <CardDescription className="text-xs leading-5">
                      {flag.description}
                    </CardDescription>
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <span>Key</span>
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">
                        {flag.settingKey}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-5"
                        aria-label={`Copy ${flag.settingKey}`}
                        onClick={() => void copyKey(flag.settingKey)}
                      >
                        <Copy className="size-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1 pt-0.5">
                    {featureFlagsQuery.isLoading ? (
                      <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    ) : featureFlagsQuery.isError ? (
                      <ShieldAlert
                        className="size-5 text-destructive-ink"
                        aria-label={`Failed to load ${flag.label}`}
                      />
                    ) : !row ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => createMutation.mutate(flag)}
                        disabled={createMutation.isPending}
                      >
                        {createMutation.isPending
                          ? "Creating..."
                          : "Create setting"}
                      </Button>
                    ) : (
                      <Switch
                        className="scale-75 origin-right"
                        checked={enabled}
                        onCheckedChange={() => handleToggle(flag, row)}
                        disabled={
                          updateMutation.isPending || createMutation.isPending
                        }
                        aria-label={`Toggle ${flag.label}`}
                      />
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-3.5 pt-0">
                {!row &&
                  !featureFlagsQuery.isLoading &&
                  !featureFlagsQuery.isError && (
                    <p className="border-t pt-3 text-xs leading-5 text-muted-foreground">
                      No setting row found — this feature is disabled until you
                      create its configuration.
                    </p>
                  )}
                {featureFlagsQuery.isError && (
                  <p className="border-t pt-3 text-xs leading-5 text-destructive-ink">
                    This setting could not be loaded. Refresh the page and try
                    again.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
        {customRows.map((row) => {
          const enabled = isFeatureFlagEnabled(row);
          return (
            <Card
              key={row.id}
              className="group overflow-hidden shadow-sm transition-shadow hover:shadow-md"
            >
              <CardHeader className="p-3.5 pb-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-sm">
                        Custom feature flag
                      </CardTitle>
                      <Badge
                        variant={enabled ? "default" : "outline"}
                        className={
                          enabled
                            ? "bg-emerald-600 text-white hover:bg-emerald-600"
                            : "border-muted-foreground/30 bg-muted/50 text-muted-foreground"
                        }
                      >
                        {enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                    <CardDescription className="text-xs leading-5">
                      {row.description || "No description provided."}
                    </CardDescription>
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <span>Key</span>
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">
                        {row.key}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-5"
                        aria-label={`Copy ${row.key}`}
                        onClick={() => void copyKey(row.key)}
                      >
                        <Copy className="size-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1 pt-0.5">
                    <Switch
                      className="scale-75 origin-right"
                      checked={enabled}
                      onCheckedChange={() =>
                        setPending({
                          flag: {
                            type: "CustomFeatureFlag",
                            label: row.key,
                            settingKey: row.key,
                            description: row.description || "",
                            offConsequence: `This custom feature flag (${row.key}) will be disabled for all clients.`,
                            key: "CustomFeatureFlag",
                          },
                          row,
                          newValue: !enabled,
                        })
                      }
                      disabled={updateMutation.isPending}
                      aria-label={`Toggle ${row.key}`}
                    />
                  </div>
                </div>
              </CardHeader>
            </Card>
          );
        })}
      </div>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add feature flag configuration</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <DialogDescription>
              Create a global setting for a client capability already supported
              by this application. New capabilities still require matching
              backend and client code.
            </DialogDescription>
            {missingFlags.length > 0 ? (
              <div className="space-y-2">
                <label
                  className="text-sm font-medium"
                  htmlFor="feature-flag-type"
                >
                  Feature flag
                </label>
                <Select
                  value={selectedFlagType}
                  onValueChange={setSelectedFlagType}
                >
                  <SelectTrigger id="feature-flag-type">
                    <SelectValue placeholder="Select a feature flag" />
                  </SelectTrigger>
                  <SelectContent>
                    {missingFlags.map((flag) => (
                      <SelectItem key={flag.type} value={flag.type}>
                        {flag.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="space-y-2 border-t pt-4">
              <p className="text-sm font-medium">Custom feature flag</p>
              <p className="text-xs text-muted-foreground">
                Add a stable key now, then use the same key when the client
                capability is implemented.
              </p>
              <Input
                value={customKey}
                onChange={(event) => setCustomKey(event.target.value)}
                placeholder="e.g. backups_enabled"
                aria-label="Custom feature flag key"
              />
              <Textarea
                value={customDescription}
                onChange={(event) => setCustomDescription(event.target.value)}
                placeholder="What this flag controls"
                aria-label="Custom feature flag description"
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            {selectedFlag && (
              <Button
                disabled={createMutation.isPending}
                onClick={() => createMutation.mutate(selectedFlag)}
              >
                {createMutation.isPending
                  ? "Creating..."
                  : "Create supported flag"}
              </Button>
            )}
            <Button
              disabled={!customKey.trim() || createCustomMutation.isPending}
              onClick={() => createCustomMutation.mutate()}
            >
              {createCustomMutation.isPending
                ? "Creating..."
                : "Create custom flag"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pending}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.newValue
                ? `Enable ${pending?.flag.label}?`
                : `Disable ${pending?.flag.label}?`}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              {pending?.newValue
                ? `This will turn ${pending?.flag.label} ON when clients next refresh their feature configuration.`
                : pending?.flag.offConsequence}
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Confirm"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
